import { z } from 'zod';

/**
 * Sovereign Validators Package (@sovereign/validators)
 * Common schema and validation utilities.
 */

export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function isValidSanskritDevanagari(text: string): boolean {
  if (!text || typeof text !== 'string') return false;
  // Check for Devanagari Unicode Block U+0900 to U+097F
  return /[\u0900-\u097F]/.test(text);
}

export const PhoneticSegmentSchema = z.object({
  syllable: z.string().min(1, 'Syllable text is required'),
  expectedPitch: z.enum(['Udatta', 'Svarita', 'Anudatta']),
  phoneticType: z.string().default('General'),
  description: z.string().default(''),
});

export const PitchZoneSchema = z.object({
  minHz: z.number(),
  maxHz: z.number(),
  label: z.string(),
  description: z.string(),
});

export const CustomMantraSchema = z.object({
  title: z.string().min(2, 'Title must be at least 2 characters'),
  sanskrit: z.string().min(1, 'Sanskrit Devanagari text is required').refine(isValidSanskritDevanagari, {
    message: 'Sanskrit text must contain valid Devanagari Unicode characters (e.g., ॐ, श्री, नमः)',
  }),
  iast: z.string().min(1, 'IAST transliteration is required'),
  translation: z.string().min(1, 'English translation is required'),
  tongue: z.enum(['sanskrit', 'pali', 'tibetan', 'nepali', 'newari']).default('sanskrit'),
  tradition: z.string().default('Vedic Practitioners'),
  significance: z.string().default('Custom practitioner mantra for daily chanting practice'),
  phoneticSegments: z.array(PhoneticSegmentSchema).optional().default([]),
  pitchZones: z.array(PitchZoneSchema).optional().default([]),
  pronunciationTips: z.array(z.string()).optional().default([]),
});

export type CustomMantraInput = z.infer<typeof CustomMantraSchema>;

export function validateCustomMantraInput(data: unknown) {
  return CustomMantraSchema.safeParse(data);
}
